/**
 * StaffLeaveWidget — all staff, leave balance (Admin/Owner). Reads the
 * existing 11E.6 all-staff summary (GET /v1/staff-attendance/summary?year=&
 * month=) rather than inventing a new aggregation — that endpoint's row
 * shape only carries a net `leave_balance` (not a separate
 * allocated/availed split), so this widget shows the net balance per staff
 * member; the full allocated/availed/balance triple is one click away on
 * that staff member's own Attendance & Leaves tab.
 *
 * data-testid: candeza-dash-staff-leave
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card, CardHeader, CardTitle } from '@/components/common';
import { useStaffAttendanceSummary } from '@features/attendance/api/staffAttendance';

const Row = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-decoration: none;
  color: inherit;
  &:last-child {
    border-bottom: none;
  }
`;

const Name = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Balance = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  margin: 0;
`;

export const StaffLeaveWidget: React.FC = () => {
  const now = new Date();
  const { data, isLoading } = useStaffAttendanceSummary(now.getFullYear(), now.getMonth() + 1);
  const rows = data?.rows ?? [];

  return (
    <Card padding="lg" data-testid="candeza-dash-staff-leave">
      <CardHeader>
        <CardTitle>Staff Leave Balance</CardTitle>
      </CardHeader>
      {isLoading && <EmptyNote>Loading…</EmptyNote>}
      {!isLoading && rows.length === 0 && <EmptyNote>No active staff.</EmptyNote>}
      {!isLoading &&
        rows.map((r) => (
          <Row key={r.staff_id} to={`/staff/${r.staff_id}`} data-testid={`candeza-dash-staff-leave-row-${r.staff_id}`}>
            <Name>{r.staff_name}</Name>
            <Balance>Balance: {r.leave_balance ?? '—'}</Balance>
          </Row>
        ))}
    </Card>
  );
};
